import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Tab,
  Tabs
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// Removed date picker imports due to compatibility issues
import api from '../api/axios';

// Define the full patient structure based on Cliniko API
interface FullPatient {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  cliniko_id?: string;
  
  // Personal Details
  title?: string;
  preferred_first_name?: string;
  date_of_birth?: string;
  gender_identity?: string;
  sex?: string;
  pronouns?: {
    accusative?: string;
    nominative?: string;
    predicative_possessive?: string;
    pronominal_possessive?: string;
    reflexive?: string;
  };
  
  // Address
  address_1?: string;
  address_2?: string;
  address_3?: string;
  city?: string;
  state?: string;
  post_code?: string;
  country?: string;
  country_code?: string;
  
  // Contact Details
  emergency_contact?: string;
  patient_phone_numbers?: Array<{
    number: string;
    phone_type: string;
  }>;
  
  // Medical Information
  occupation?: string;
  medicare?: string;
  medicare_reference_number?: string;
  dva_card_number?: string;
  concession_type_id?: string;
  referring_doctor_id?: string;
  referral_source?: string;
  
  // Billing & Communication
  invoice_default_to?: string;
  invoice_email?: string;
  invoice_extra_information?: string;
  appointment_notes?: string;
  notes?: string;
  time_zone?: string;
  
  // Preferences
  accepted_email_marketing?: boolean;
  accepted_privacy_policy?: boolean;
  receives_cancellation_emails?: boolean;
  receives_confirmation_emails?: boolean;
  reminder_type?: string;
  unsubscribe_sms_marketing?: boolean;
  
  // Custom Fields
  custom_fields?: {
    sections: Array<{
      name: string;
      token: string;
      archived: boolean;
      fields: Array<{
        value: string;
        archived: boolean;
        name: string;
        token: string;
        type: string;
      }>;
    }>;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface PatientEditFormProps {
  patientId: string;
  onSave?: () => void;
  onCancel?: () => void;
}

const PatientEditForm: React.FC<PatientEditFormProps> = ({ patientId, onSave, onCancel }) => {
  const [patient, setPatient] = useState<FullPatient | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [phoneNumbers, setPhoneNumbers] = useState<Array<{number: string, phone_type: string}>>([]);

  // Phone type options
  const phoneTypes = ["Home", "Work", "Mobile", "Fax", "Other"];

  useEffect(() => {
    const fetchPatientDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // First fetch basic patient data
        const basicResponse = await api.get(`/api/patients/${patientId}`);
        const basicData = basicResponse.data;
        
        // Now fetch complete patient data from Cliniko if we have a Cliniko ID
        if (basicData.cliniko_id) {
          try {
            const detailedResponse = await api.get(`/api/cliniko/patients/${basicData.cliniko_id}`);
            const completeData = detailedResponse.data;
            
            // Set phone numbers for separate state management
            if (completeData.patient_phone_numbers && completeData.patient_phone_numbers.length > 0) {
              setPhoneNumbers(completeData.patient_phone_numbers);
            } else {
              setPhoneNumbers([{ number: '', phone_type: 'Mobile' }]);
            }
            
            setPatient(completeData);
          } catch (detailError) {
            console.error('Error fetching detailed patient data:', detailError);
            // Fall back to basic data if detailed fetch fails
            setPatient(basicData);
            setPhoneNumbers([{ number: '', phone_type: 'Mobile' }]);
          }
        } else {
          // No Cliniko ID, just use basic data
          setPatient(basicData);
          setPhoneNumbers([{ number: '', phone_type: 'Mobile' }]);
        }
      } catch (error) {
        console.error('Error fetching patient data:', error);
        setError('Failed to load patient data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (patientId) {
      fetchPatientDetails();
    }
  }, [patientId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Handle nested properties with dot notation
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setPatient(prev => {
        if (!prev) return null;
        
        return {
          ...prev,
          [parent]: {
            ...prev[parent as keyof FullPatient],
            [child]: value
          }
        };
      });
    } else {
      setPatient(prev => {
        if (!prev) return null;
        return { ...prev, [name]: value };
      });
    }
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setPatient(prev => {
      if (!prev) return null;
      return { ...prev, [name]: value };
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setPatient(prev => {
      if (!prev) return null;
      return { ...prev, [name]: checked };
    });
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const { value } = e.target;
    
    setPatient(prev => {
      if (!prev) return null;
      return { ...prev, [fieldName]: value };
    });
  };

  // Phone number handlers
  const handleAddPhoneNumber = () => {
    setPhoneNumbers([...phoneNumbers, { number: '', phone_type: 'Mobile' }]);
  };

  const handleRemovePhoneNumber = (index: number) => {
    const newPhoneNumbers = [...phoneNumbers];
    newPhoneNumbers.splice(index, 1);
    setPhoneNumbers(newPhoneNumbers);
  };

  const handlePhoneNumberChange = (index: number, field: 'number' | 'phone_type', value: string) => {
    const newPhoneNumbers = [...phoneNumbers];
    newPhoneNumbers[index][field] = value;
    setPhoneNumbers(newPhoneNumbers);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      if (!patient) {
        setError('No patient data to save');
        return;
      }
      
      // Add the phone numbers to the patient data
      const patientToSave = {
        ...patient,
        patient_phone_numbers: phoneNumbers.filter(phone => phone.number.trim() !== '')
      };
      
      let response;
      
      if (patient.cliniko_id) {
        // Update existing patient on Cliniko
        response = await api.patch(`/api/cliniko/patients/${patient.cliniko_id}`, patientToSave);
      } else {
        // Create new patient on Cliniko
        response = await api.post('/api/cliniko/patients', patientToSave);
      }
      
      setSuccess('Patient information saved successfully!');
      setEditMode(false);
      
      // Call the onSave callback if provided
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error('Error saving patient data:', error);
      setError('Failed to save patient data. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!patient) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Patient not found or could not be loaded.
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ mb: 3, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="h2">
          {editMode ? 'Edit Patient Information' : 'Patient Information'}
        </Typography>
        <Box>
          {!editMode ? (
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<EditIcon />}
              onClick={() => setEditMode(true)}
            >
              Edit Patient
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="outlined" 
                color="error" 
                startIcon={<CancelIcon />}
                onClick={() => {
                  setEditMode(false);
                  if (onCancel) onCancel();
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="patient information tabs">
          <Tab label="Basic Info" id="tab-0" />
          <Tab label="Contact Details" id="tab-1" />
          <Tab label="Medical Info" id="tab-2" />
          <Tab label="Preferences" id="tab-3" />
        </Tabs>
      </Box>

      {/* Basic Info Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Title"
              name="title"
              value={patient.title || ''}
              onChange={handleInputChange}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="First Name"
              name="first_name"
              value={patient.first_name || ''}
              onChange={handleInputChange}
              required
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Last Name"
              name="last_name"
              value={patient.last_name || ''}
              onChange={handleInputChange}
              required
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Preferred Name"
              name="preferred_first_name"
              value={patient.preferred_first_name || ''}
              onChange={handleInputChange}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Date of Birth (YYYY-MM-DD)"
              name="date_of_birth"
              value={patient.date_of_birth || ''}
              onChange={(e) => handleDateChange(e, 'date_of_birth')}
              placeholder="YYYY-MM-DD"
              disabled={!editMode}
              helperText="Format: YYYY-MM-DD (e.g., 1980-01-31)"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Gender Identity"
              name="gender_identity"
              value={patient.gender_identity || ''}
              onChange={handleInputChange}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={!editMode}>
              <InputLabel id="sex-label">Sex</InputLabel>
              <Select
                labelId="sex-label"
                name="sex"
                value={patient.sex || ''}
                label="Sex"
                onChange={handleSelectChange}
              >
                <MenuItem value="">Not specified</MenuItem>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Hydralyfe ID"
              value={patient.cliniko_id || 'Not linked'}
              disabled={true}
            />
          </Grid>
        </Grid>
      </TabPanel>

      {/* Contact Details Tab */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={patient.email || ''}
              onChange={handleInputChange}
              required
              disabled={!editMode}
            />
          </Grid>
          
          {/* Phone Numbers Section */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>Phone Numbers</Typography>
            {phoneNumbers.map((phone, index) => (
              <Box key={index} sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  label="Phone Number"
                  value={phone.number}
                  onChange={(e) => handlePhoneNumberChange(index, 'number', e.target.value)}
                  disabled={!editMode}
                  sx={{ flexGrow: 1 }}
                />
                <FormControl sx={{ minWidth: 120 }} disabled={!editMode}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={phone.phone_type}
                    onChange={(e) => handlePhoneNumberChange(index, 'phone_type', e.target.value)}
                    label="Type"
                  >
                    {phoneTypes.map(type => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {editMode && (
                  <IconButton 
                    color="error" 
                    onClick={() => handleRemovePhoneNumber(index)}
                    disabled={phoneNumbers.length === 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            ))}
            {editMode && (
              <Button 
                startIcon={<AddIcon />} 
                variant="outlined" 
                onClick={handleAddPhoneNumber}
                sx={{ mt: 1 }}
              >
                Add Phone Number
              </Button>
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Emergency Contact"
              name="emergency_contact"
              value={patient.emergency_contact || ''}
              onChange={handleInputChange}
              multiline
              rows={2}
              disabled={!editMode}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>Address</Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 1"
              name="address_1"
              value={patient.address_1 || ''}
              onChange={handleInputChange}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 2"
              name="address_2"
              value={patient.address_2 || ''}
              onChange={handleInputChange}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="City"
              name="city"
              value={patient.city || ''}
              onChange={handleInputChange}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="State"
              name="state"
              value={patient.state || ''}
              onChange={handleInputChange}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Postal Code"
              name="post_code"
              value={patient.post_code || ''}
              onChange={handleInputChange}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Country"
              name="country"
              value={patient.country || ''}
              onChange={handleInputChange}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={!editMode}>
              <InputLabel id="country-code-label">Country Code</InputLabel>
              <Select
                labelId="country-code-label"
                name="country_code"
                value={patient.country_code || 'AU'}
                label="Country Code"
                onChange={handleSelectChange}
              >
                <MenuItem value="AU">Australia (AU)</MenuItem>
                <MenuItem value="US">United States (US)</MenuItem>
                <MenuItem value="GB">United Kingdom (GB)</MenuItem>
                <MenuItem value="NZ">New Zealand (NZ)</MenuItem>
                {/* Add other country codes as needed */}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Medical Info Tab */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Occupation"
              name="occupation"
              value={patient.occupation || ''}
              onChange={handleInputChange}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Medicare Number"
              name="medicare"
              value={patient.medicare || ''}
              onChange={handleInputChange}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Medicare Reference Number"
              name="medicare_reference_number"
              value={patient.medicare_reference_number || ''}
              onChange={handleInputChange}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="DVA Card Number"
              name="dva_card_number"
              value={patient.dva_card_number || ''}
              onChange={handleInputChange}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Referral Source"
              name="referral_source"
              value={patient.referral_source || ''}
              onChange={handleInputChange}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Appointment Notes"
              name="appointment_notes"
              value={patient.appointment_notes || ''}
              onChange={handleInputChange}
              multiline
              rows={2}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="General Notes"
              name="notes"
              value={patient.notes || ''}
              onChange={handleInputChange}
              multiline
              rows={4}
              disabled={!editMode}
            />
          </Grid>
        </Grid>
      </TabPanel>

      {/* Preferences Tab */}
      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>Communication Preferences</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={patient.receives_confirmation_emails || false}
                  onChange={handleCheckboxChange}
                  name="receives_confirmation_emails"
                  disabled={!editMode}
                />
              }
              label="Send Appointment Confirmation Emails"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={patient.receives_cancellation_emails || false}
                  onChange={handleCheckboxChange}
                  name="receives_cancellation_emails"
                  disabled={!editMode}
                />
              }
              label="Send Appointment Cancellation Emails"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={patient.accepted_email_marketing || false}
                  onChange={handleCheckboxChange}
                  name="accepted_email_marketing"
                  disabled={!editMode}
                />
              }
              label="Accept Email Marketing"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!patient.unsubscribe_sms_marketing}
                  onChange={(e) => {
                    setPatient(prev => {
                      if (!prev) return null;
                      return { ...prev, unsubscribe_sms_marketing: !e.target.checked };
                    });
                  }}
                  name="sms_marketing"
                  disabled={!editMode}
                />
              }
              label="Accept SMS Marketing"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={patient.accepted_privacy_policy || false}
                  onChange={handleCheckboxChange}
                  name="accepted_privacy_policy"
                  disabled={!editMode}
                />
              }
              label="Accepted Privacy Policy"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={!editMode}>
              <InputLabel id="reminder-type-label">Reminder Type</InputLabel>
              <Select
                labelId="reminder-type-label"
                name="reminder_type"
                value={patient.reminder_type || 'None'}
                label="Reminder Type"
                onChange={handleSelectChange}
              >
                <MenuItem value="None">None</MenuItem>
                <MenuItem value="Email">Email</MenuItem>
                <MenuItem value="SMS">SMS</MenuItem>
                <MenuItem value="Both">Both Email and SMS</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={!editMode}>
              <InputLabel id="timezone-label">Time Zone</InputLabel>
              <Select
                labelId="timezone-label"
                name="time_zone"
                value={patient.time_zone || 'Australia/Melbourne'}
                label="Time Zone"
                onChange={handleSelectChange}
              >
                <MenuItem value="Australia/Melbourne">Melbourne</MenuItem>
                <MenuItem value="Australia/Sydney">Sydney</MenuItem>
                <MenuItem value="Australia/Brisbane">Brisbane</MenuItem>
                <MenuItem value="Australia/Perth">Perth</MenuItem>
                <MenuItem value="Australia/Adelaide">Adelaide</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>Billing Information</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Invoice Default To"
              name="invoice_default_to"
              value={patient.invoice_default_to || ''}
              onChange={handleInputChange}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Invoice Email"
              name="invoice_email"
              value={patient.invoice_email || ''}
              onChange={handleInputChange}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Invoice Extra Information"
              name="invoice_extra_information"
              value={patient.invoice_extra_information || ''}
              onChange={handleInputChange}
              multiline
              rows={2}
              disabled={!editMode}
            />
          </Grid>
        </Grid>
      </TabPanel>
      
      {/* Save/Cancel buttons at bottom if in edit mode */}
      {editMode && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
          <Button 
            variant="outlined" 
            color="error" 
            onClick={() => {
              setEditMode(false);
              if (onCancel) onCancel();
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PatientEditForm;